/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { CONTENT_TYPE_JSON } from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { getGenericErrorMessage } from "../../utils/genericErrorMessages.js";
/**
 * @fileoverview Authentication logic module.
 * Handles user authentication, SSO login flow, and role data retrieval.
 * @module logic/auth
 */

/**
 * Fetches user role and permission data from the API.
 * @async
 * @param {Object} data - Request data object
 * @param {string} data.token - Authentication token
 * @param {string} data.userId - User identifier
 * @returns {Promise<Object>} User role and permission data
 * @throws {Error} If the API request fails
 */
export const getUserRoleData = async (data) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${data.token}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${data.userId}`,
      options,
    );
    const userRoleData = await response.json();
    logger.info("getUserRoleData permission data: ", { data: userRoleData });
    return userRoleData;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    throw error;
  }
};

/**
 * Initiates SSO (Single Sign-On) login flow by redirecting to Cognito/Azure AD.
 * @async
 * @returns {Promise<void>} Redirects the browser to the SSO login URL
 */
export const handleSSOLogin = async () => {
  logger.info("Initiating SSO login...");
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
    },
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}auth/sso/url`,
      options,
    );
    const data = await response.json();
    logger.info("SSO login data: ", { data: data });

    if (!response.ok) {
      throw new Error(getGenericErrorMessage("SSO_LOGIN_FAILED"));
    }

    // Redirect the browser to Cognito (this triggers SSO via Azure AD)
    window.location.href = data.url;
  } catch (error) {
    logger.error("Error during SSO login:", { err: sanitizeError(error) });
  }
};

/**
 * Handles the SSO callback after successful authentication.
 * Exchanges the authorization code for tokens and user information.
 * @async
 * @param {string} code - Authorization code from the SSO provider
 * @returns {Promise<Object>} Authentication data including tokens and user info
 * @throws {Error} If the callback handling fails
 */
export const handleSSOCallback = async (code) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
    },
    body: JSON.stringify({ code }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}auth/sso/callback`,
      options,
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(getGenericErrorMessage("SSO_CALLBACK_FAILED"));
    }

    return data; // Return the authentication data (tokens, user info, etc.)
  } catch (error) {
    logger.error("Error during SSO callback handling:", {
      err: sanitizeError(error),
    });
    throw error;
  }
};
